import { useEffect, useRef, useState } from "react";
import { Canvas } from "./components/Canvas/Canvas";
import type { CanvasHandle } from "./components/Canvas/Canvas";
import { Toolbar } from "./components/Toolbar/Toolbar";
import { ColorPalette } from "./components/ColorPalette/ColorPalette";
import { BrushSizePicker } from "./components/BrushSizePicker/BrushSizePicker";
import { ImagePicker } from "./components/ImagePicker/ImagePicker";
import { ImageSizePicker } from "./components/ImageSizePicker/ImageSizePicker";
import { ShapeKindPicker } from "./components/ShapeKindPicker/ShapeKindPicker";
import { ParentGate } from "./components/ParentGate/ParentGate";
import { CollapsiblePanel } from "./components/CollapsiblePanel/CollapsiblePanel";
import { usePaintStore } from "./state/paintStore";
import { saveCanvasAsPng } from "./utils/saveCanvas";
import "./App.css";

type PendingAction = "clear" | null;

function App() {
  const canvasRef = useRef<CanvasHandle>(null);
  const activeTool = usePaintStore((s) => s.activeTool);
  const imageSize = usePaintStore((s) => s.imageSize);
  const openPanel = usePaintStore((s) => s.openPanel);
  const togglePanel = usePaintStore((s) => s.togglePanel);
  const closePanel = usePaintStore((s) => s.closePanel);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [, setTick] = useState(0);

  const refresh = () => setTick((n) => n + 1);

  const showsBrush =
    activeTool === "marker" ||
    activeTool === "eraser" ||
    activeTool === "shape";
  const showsImageSize = activeTool === "image";
  const showsShape = activeTool === "shape";

  useEffect(() => {
    if (activeTool === "shape" && openPanel !== "shape") {
      usePaintStore.setState({ openPanel: "shape" });
    }
  }, [activeTool, openPanel]);

  const handleSave = () => {
    const canvas = canvasRef.current?.getCanvas();
    if (canvas) saveCanvasAsPng(canvas);
  };

  const handleParentConfirm = () => {
    if (pendingAction === "clear") {
      canvasRef.current?.clear();
      refresh();
    }
    setPendingAction(null);
  };

  const handlePickImage = async (path: string) => {
    await canvasRef.current?.stampImage(path, imageSize.scale);
    refresh();
    closePanel();
  };

  return (
    <div className="app-shell">
      <header className="app-titlebar">
        <span className="app-title">Paint</span>
      </header>
      <main className="app-main">
        <Toolbar
          onUndo={() => {
            canvasRef.current?.undo();
            refresh();
          }}
          onRedo={() => {
            canvasRef.current?.redo();
            refresh();
          }}
          canUndo={canvasRef.current?.canUndo ?? false}
          canRedo={canvasRef.current?.canRedo ?? false}
          onSave={handleSave}
          onRequestClear={() => setPendingAction("clear")}
        />
        <Canvas ref={canvasRef} />
      </main>
      <footer className="app-footer">
        <CollapsiblePanel
          title="Colors"
          open={openPanel === "colors"}
          onToggle={() => togglePanel("colors")}
        >
          <ColorPalette />
        </CollapsiblePanel>
        {showsShape && (
          <CollapsiblePanel
            title="Shape"
            open={openPanel === "shape"}
            onToggle={() => togglePanel("shape")}
          >
            <ShapeKindPicker />
          </CollapsiblePanel>
        )}
        {showsBrush && (
          <CollapsiblePanel
            title="Brush size"
            open={openPanel === "brush"}
            onToggle={() => togglePanel("brush")}
          >
            <BrushSizePicker />
          </CollapsiblePanel>
        )}
        {showsImageSize && (
          <CollapsiblePanel
            title="Image size"
            open={openPanel === "imageSize"}
            onToggle={() => togglePanel("imageSize")}
          >
            <ImageSizePicker />
          </CollapsiblePanel>
        )}
      </footer>
      {openPanel === "images" && (
        <ImagePicker onPick={handlePickImage} onClose={closePanel} />
      )}
      {pendingAction === "clear" && (
        <ParentGate
          title="Clear the drawing?"
          onConfirm={handleParentConfirm}
          onCancel={() => setPendingAction(null)}
        />
      )}
    </div>
  );
}

export default App;
